import { BuyerCustomerDTO, Permission, CustomerDetailsDTO, Res, InvoiceDTO } from 'core/api';
import { CountryNames, Result } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { Command } from 'pos/common';

import * as React from 'react';

import * as UI from 'core/ui';
import * as Images from 'pos/Images';

import { PosScreenClassComponent, InfoCard, ScreenSize, ActionButtonDefinition } from 'pos/ui';
import * as InvoiceStore from './InvoiceStore';
import { CustomerFacade } from 'pos/facades';
import { Flex } from 'core/ui';
import { Message, Modal } from 'core/ui-next/modal';

type Props = {
	currentCustomerDetails: CustomerDetailsDTO;
	invoiceCustomer: BuyerCustomerDTO;
} & Modal.Actions<void>;

type State = {
	customer: CustomerDetailsDTO;
};

class ConfirmNewCustomerData extends PosScreenClassComponent<Props, State> {


	protected getScreenId(): string {
		return 'UPDATE_INVOICE_CUSTOMER';
	}

	protected getTitle(): string {
		return Res.Web.UPDATE_CUSTOMER_DATA();
	}

	protected getImage(): string | SvgIcon {
		return Images.UpdateCustomer;
	}

	protected getSize(): ScreenSize {
		return 'medium';
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		return [
			this.cancelButton(),
			null,
			null,
			null,
			null,
			this.okButton(this.onOK)
		];
	}

	private updateCustomer = async (): Promise<void> => {
		const result = await Command.execute('editCustomer', this.props.currentCustomerDetails.Id);
		if (result.ok) {
			const customer = await CustomerFacade.getDetails(result.value.Id);
			this.setState({ customer });
		}
	};

	protected useKeypad() {
		return false;
	}

	createInitialState(props: Props): Nullable<State> {
		return { customer: props.currentCustomerDetails };
	}

	onComponentDidMount(): void {
	}

	onComponentWillUnmount(): void {
	}

	private onOK = async () => {
		this.close();
	};

	protected getContent(): React.ReactNode {
		const { invoiceCustomer: buyerCustomer } = this.props;
		const { customer } = this.state;

		return <Flex.Horizontal>
			<Flex.Item className='p-sm' size='*'>
				<Flex.Vertical>
					<Flex.Item>
						<h2>{Res.Web.UPDATE_CUSTOMER_DATA_CUSTOMER_IN_DOCUMENT_GRP()}</h2>
					</Flex.Item>
					<Flex.Item>
						<InfoCard Icon={Images.Customer} title={buyerCustomer.FiscalName} lines={[buyerCustomer.BusinessName, buyerCustomer.Cif, CountryNames.getName(buyerCustomer.CountryCode)]} />
					</Flex.Item>
					<Flex.Item>
						<InfoCard Icon={Images.Location} title={buyerCustomer.Address.Street} lines={[buyerCustomer.Address.City, `${buyerCustomer.Address.ZipCode} ${buyerCustomer.Address.Region}`]} />
					</Flex.Item>
				</Flex.Vertical>
			</Flex.Item>
			<Flex.Item className='p-sm' size='*'>
				<Flex.Vertical>
					<Flex.Item>
						<h2>{Res.Web.UPDATE_CUSTOMER_DATA_CUSTOMER_IN_DATABASE_GRP()}
							<UI.Button onClick={this.updateCustomer} buttonStyle='info' outline tabIndex={-1} className='m-l-md'>
								{Res.Web.UPDATE_CUSTOMER_DATA_EDIT_CUSTOMER_BTN()}
							</UI.Button>
						</h2>
					</Flex.Item>
					<Flex.Item>
						<InfoCard Icon={Images.Customer} title={customer.FiscalName} lines={[customer.BusinessName, customer.Cif, CountryNames.getName(customer.CountryCode)]} />
					</Flex.Item>
					<Flex.Item>
						<InfoCard Icon={Images.Location} title={customer.Address.Street} lines={[customer.Address.City, `${customer.Address.ZipCode} ${customer.Address.Region}`]} />
					</Flex.Item>
				</Flex.Vertical>
			</Flex.Item>
		</Flex.Horizontal >;
	}
}

declare global {
	interface CommandType {
		updateInvoiceCustomer: (invoice: InvoiceDTO) => Promise<Result>
	}
}

Command.register('updateInvoiceCustomer', Preconditions.and(Preconditions.ensure(Permission.Ticket_UpdateCustomerData), Preconditions.ensureIsNotLite), async (invoice: InvoiceDTO): Promise<Result> => {
	if (invoice.Customer.Id === 0)
		return Command.execute('setOrRemoveCustomer', {
			customer: invoice.Customer,
			requiredPermission: Permission.Ticket_SetBuyerCustomer,
			setCustomer: (customer: BuyerCustomerDTO) => InvoiceStore.setCustomer(invoice.Id, customer)
		});

	if (invoice.HasTicketBAIData) {
		await Message.show(Res.Msg.MSG_CANNOT_CHANGE_OR_UPDATE_CUSTORMER_FROM_INVOICE_WITH_TICKET_BAI());
		return Result.error();
	}

	if (invoice.HasVerifactuData) {
		await Message.show(Res.Msg.MSG_CANNOT_CHANGE_OR_UPDATE_CUSTORMER_FROM_INVOICE_WITH_VERIFACTU());
		return Result.error();
	}

	const currentCustomerData = await CustomerFacade.getDetails(invoice.Customer.Id);

	const result = await Modal.show(ConfirmNewCustomerData,
		{
			invoiceCustomer: invoice.Customer,
			currentCustomerDetails: currentCustomerData
		});
	if (!result.ok)
		return Result.error();

	await InvoiceStore.updateCustomerData(invoice.Id);
	return Result.ok();
});
